// CB 股價 K 線圖控制器
// 使用 TradingView Lightweight Charts 繪製 OHLC K 線圖與成交量
// 支援時間區間切換 (1M / 3M / 6M / 1Y / 2Y / 5Y)

import { createChart, CandlestickSeries, HistogramSeries } from 'lightweight-charts'
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ['container', 'rangeButton', 'legend']
  static values = {
    stockId: Number,
    showOnConnected: { type: Boolean, default: false }
  }

  // 圖表配置常數 - 精緻淺色主題
  // 台灣市場配色：紅漲綠跌（略微降低彩度）
  static COLORS = {
    // K 線 - 稍微柔和的紅綠色
    up: '#c93c3c',        // 柔和紅（漲）
    down: '#2a9d58',      // 柔和綠（跌）
    upWick: '#d95050',    // 柔和紅（上影線）
    downWick: '#3dab68',  // 柔和綠（下影線）

    // 成交量 - 半透明，視覺上較輕
    volumeUp: 'rgba(201, 60, 60, 0.25)',
    volumeDown: 'rgba(42, 157, 88, 0.25)',

    // 圖表元素
    background: '#ffffff',
    grid: '#f1f5f9',       // slate-100
    border: '#e2e8f0',     // slate-200
    text: '#64748b',       // slate-500
    crosshairLine: '#cbd5e1', // slate-300
    crosshairLabel: '#475569' // slate-600
  }

  // 時間區間對應的天數
  static RANGES = {
    '1M': 30,
    '3M': 90,
    '6M': 180,
    '1Y': 365,
    '2Y': 730,
    '5Y': 1825
  }

  static DEFAULT_RANGE = '6M'

  // 滾輪縮放設定
  static ZOOM_CONFIG = {
    factor: 0.1,    // 每次縮放 10%
    minBars: 10,    // 最少顯示 10 根 K 棒
    maxBars: 2000   // 最多顯示 2000 根 K 棒
  }

  // 按鈕樣式類別
  static BUTTON_CLASSES = {
    active: ['bg-white', 'text-slate-900', 'shadow-sm', 'ring-1', 'ring-slate-200'],
    inactive: ['text-slate-500', 'hover:text-slate-700', 'hover:bg-slate-50']
  }

  // 響應式配置（對應 Tailwind sm breakpoint）
  static RESPONSIVE = {
    breakpoint: 640,  // px
    fontSize: {
      mobile: 10,
      desktop: 11
    }
  }

  // 預設值
  static DEFAULTS = {
    chartHeight: 340  // 當容器無 CSS 高度時的備用值
  }

  // === Static Data Transformation Methods ===
  // 純函數，便於單元測試，不依賴 DOM 或圖表實例

  /**
   * 轉換 API 回傳資料為 K 線圖格式
   * @param {Array} data - API 回傳的原始資料
   * @returns {Array} Lightweight Charts 所需的 OHLC 資料格式
   */
  static transformCandlestickData(data) {
    return data.map(d => ({
      time: d.time,
      open: d.open,
      high: d.high,
      low: d.low,
      close: d.close
    }))
  }

  /**
   * 轉換 API 回傳資料為成交量圖格式
   * 根據漲跌設定對應顏色（台灣市場：漲紅跌綠）
   * @param {Array} data - API 回傳的原始資料
   * @param {Object} colors - 顏色配置物件，需包含 volumeUp 和 volumeDown
   * @returns {Array} Lightweight Charts 所需的成交量資料格式
   */
  static transformVolumeData(data, colors) {
    return data.map(d => ({
      time: d.time,
      value: d.volume || 0,
      color: d.close >= d.open ? colors.volumeUp : colors.volumeDown
    }))
  }

  // === Static Legend Formatter Methods ===
  // 純函數，便於單元測試，不依賴 DOM 或圖表實例

  /**
   * 格式化成交量數值
   * 使用千分位逗號格式，適合台灣市場閱讀習慣
   *
   * @param {number} volume - 原始成交量數字
   * @returns {string} 格式化後的成交量字串，無資料時回傳 '-'
   */
  static formatVolume(volume) {
    if (volume === null || volume === undefined || volume === 0) {
      return '-'
    }
    return new Intl.NumberFormat('zh-TW').format(Math.round(volume))
  }

  /**
   * 格式化價格數值
   * 保留兩位小數，適合股價顯示
   *
   * @param {number} price - 原始價格數字
   * @returns {string} 格式化後的價格字串，無資料時回傳 '-'
   */
  static formatPrice(price) {
    if (price === null || price === undefined) {
      return '-'
    }
    return price.toFixed(2)
  }

  /**
   * 格式化日期用於 Legend 顯示
   * 格式：Mon 24/12/30 (星期縮寫 + 年/月/日)
   *
   * @param {string} time - YYYY-MM-DD 格式的日期字串
   * @returns {string} 格式化後的日期字串
   */
  static formatLegendDate(time) {
    const date = new Date(time)
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const dayName = days[date.getUTCDay()]
    const yy = String(date.getUTCFullYear()).slice(-2)
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0')
    const dd = String(date.getUTCDate()).padStart(2, '0')
    return `${dayName} ${yy}/${mm}/${dd}`
  }

  /**
   * 從 crosshair 事件參數建構 Legend 資料物件
   * 用於將 Lightweight Charts 的 crosshair 事件資料轉換為 UI 顯示格式
   *
   * @param {Object} param - crosshair move 事件參數
   * @param {Object} candlestickSeries - K 線 series 參考
   * @param {Object} volumeSeries - 成交量 series 參考
   * @returns {Object|null} Legend 資料物件，無資料時回傳 null
   */
  static buildLegendData(param, candlestickSeries, volumeSeries) {
    if (!param.time || !param.seriesData) {
      return null
    }

    const ohlcData = param.seriesData.get(candlestickSeries)
    if (!ohlcData) {
      return null
    }

    const volumeData = param.seriesData.get(volumeSeries)

    return {
      time: param.time,
      open: ohlcData.open,
      high: ohlcData.high,
      low: ohlcData.low,
      close: ohlcData.close,
      volume: volumeData ? volumeData.value : 0,
      isUp: ohlcData.close >= ohlcData.open
    }
  }

  connect() {
    this.chartLoaded = false
    this.currentRange = this.constructor.DEFAULT_RANGE

    if (this.showOnConnectedValue) {
      this.loadChart()
    }
  }

  disconnect() {
    this._cleanup()
  }

  // 載入圖表資料並繪製
  loadChart() {
    if (this.chartLoaded && this.chart) {
      return
    }

    this._renderLoadingState()
    const days = this.constructor.RANGES[this.currentRange]
    this._fetchAndRender(days)
  }

  // 切換時間區間（由按鈕觸發）
  switchRange(event) {
    const newRange = event.currentTarget.dataset.range
    if (newRange === this.currentRange) {
      return
    }

    this.currentRange = newRange
    this._updateRangeButtonStyles()

    this._renderLoadingState()
    const days = this.constructor.RANGES[newRange]
    this._fetchAndRender(days)
  }

  // === Private Methods ===

  _fetchAndRender(days) {
    const url = `/api/prices/${this.stockIdValue}?days=${days}`

    fetch(url)
      .then(response => response.json())
      .then(json => {
        if (json.status === 'OK' && json.data.length > 0) {
          this._createChart(json.data)
        } else {
          this._renderNoDataMessage()
        }
      })
      .catch(error => {
        console.error('Error fetching price data:', error)
        this._renderErrorMessage()
      })
  }

  _createChart(data) {
    // 清理既有圖表
    this._cleanup()

    // 儲存原始資料供 Legend 使用
    this.chartData = data

    const container = this.containerTarget
    // 清除 loading state 的 HTML，避免與圖表重疊
    container.innerHTML = ''
    const colors = this.constructor.COLORS

    // 建立圖表 - 精緻淺色主題
    // 高度由 CSS 控制（Tailwind 響應式 class），JS 讀取實際值
    this.chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight || this.constructor.DEFAULTS.chartHeight,
      localization: {
        // 自訂 crosshair label 日期格式：Mon 25/01/06
        timeFormatter: (time) => {
          const date = new Date(time)
          const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
          const dayName = days[date.getUTCDay()]
          const yy = String(date.getUTCFullYear()).slice(-2)
          const mm = String(date.getUTCMonth() + 1).padStart(2, '0')
          const dd = String(date.getUTCDate()).padStart(2, '0')
          return `${dayName} ${yy}/${mm}/${dd}`
        }
      },
      layout: {
        background: { type: 'solid', color: colors.background },
        textColor: colors.text,
        fontFamily: "'Inter', 'Noto Sans TC', system-ui, sans-serif",
        ...this._getResponsiveLayout()
      },
      grid: {
        vertLines: {
          color: colors.grid,
          style: 1 // Solid
        },
        horzLines: {
          color: colors.grid,
          style: 1
        }
      },
      rightPriceScale: {
        borderColor: colors.border,
        scaleMargins: {
          top: 0.08,
          bottom: 0.22
        },
        borderVisible: false
      },
      timeScale: {
        borderColor: colors.border,
        timeVisible: true,
        secondsVisible: false,
        borderVisible: false,
        fixLeftEdge: true,
        fixRightEdge: true
      },
      crosshair: {
        mode: 1, // Magnet mode
        vertLine: {
          color: colors.crosshairLine,
          width: 1,
          style: 2, // Dashed
          labelBackgroundColor: colors.crosshairLabel
        },
        horzLine: {
          color: colors.crosshairLine,
          width: 1,
          style: 2,
          labelBackgroundColor: colors.crosshairLabel
        }
      },
      handleScale: {
        mouseWheel: false,  // 關閉預設「縮放」功能，改用自訂的右邊緣固定縮放
        axisPressedMouseMove: {
          time: true,
          price: true
        }
      },
      handleScroll: {
        mouseWheel: true,   // 保留滾輪平移功能
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: false
      }
    })

    // 新增 K 線 Series - 精緻配色
    this.candlestickSeries = this.chart.addSeries(CandlestickSeries, {
      upColor: colors.up,
      downColor: colors.down,
      borderVisible: false,
      wickUpColor: colors.upWick,
      wickDownColor: colors.downWick
    })

    // 處理 K 線資料（使用靜態方法，便於單元測試）
    const candlestickData = this.constructor.transformCandlestickData(data)
    this.candlestickSeries.setData(candlestickData)

    // 新增成交量 Series（作為 overlay）
    this.volumeSeries = this.chart.addSeries(HistogramSeries, {
      priceFormat: {
        type: 'volume'
      },
      priceScaleId: '' // 設為 overlay
    })

    // 設定成交量位置（佔底部 18%）
    // 隱藏成交量的 Y 軸標籤（已有 Legend 顯示數值，不需重複）
    this.volumeSeries.priceScale().applyOptions({
      scaleMargins: {
        top: 0.85,
        bottom: 0
      },
      visible: false
    })

    // 處理成交量資料（使用靜態方法，便於單元測試）
    const volumeData = this.constructor.transformVolumeData(data, colors)
    this.volumeSeries.setData(volumeData)

    // 自動調整時間軸
    this.chart.timeScale().fitContent()

    // 設定響應式調整
    this._setupResizeHandler()

    // 設定右邊緣固定的滾輪縮放
    this._setupWheelZoom()

    // 設定 OHLCV Legend 面板
    this._renderLegendElement()
    this._setupLegendHandler()
    this._updateLegend(this._getLatestLegendData())

    this.chartLoaded = true
  }

  _setupResizeHandler() {
    const DEBOUNCE_DELAY = 150

    // 共用的 resize 處理邏輯
    // 直接使用 CSS 約束後的容器寬度，由 max-w-[calc(100vw-1rem)] 確保不超出 viewport
    const handleResize = () => {
      clearTimeout(this._resizeTimeout)
      this._resizeTimeout = setTimeout(() => {
        if (this.chart && this.containerTarget) {
          const containerWidth = this.containerTarget.clientWidth
          const containerHeight = this.containerTarget.clientHeight

          // 使用官方 resize() 方法調整圖表尺寸
          // 參考: https://github.com/tradingview/lightweight-charts
          this.chart.resize(containerWidth, containerHeight)
          this.chart.applyOptions({
            layout: this._getResponsiveLayout()
          })
        }
      }, DEBOUNCE_DELAY)
    }

    // ResizeObserver: 偵測容器大小變化（一般情況）
    this.resizeObserver = new ResizeObserver(handleResize)
    this.resizeObserver.observe(this.containerTarget)

    // Window resize: 偵測 viewport 變化
    // 處理容器在表格內（overflow-auto）不會隨 viewport 縮小的情況
    // 例如：手機螢幕旋轉、DevTools 裝置切換
    this.windowResizeHandler = handleResize
    window.addEventListener('resize', this.windowResizeHandler)
  }

  // 設定右邊緣固定的滾輪縮放
  // 預設的 Lightweight Charts 縮放以中央為基準，金融圖表習慣固定右邊緣（最新日期）
  _setupWheelZoom() {
    this.wheelHandler = (event) => {
      event.preventDefault()

      const currentRange = this.chart.timeScale().getVisibleLogicalRange()
      if (!currentRange) return

      const currentBars = currentRange.to - currentRange.from
      const { factor, minBars, maxBars } = this.constructor.ZOOM_CONFIG

      // deltaY > 0 表示向下滾（縮小），< 0 表示向上滾（放大）
      const zoomIn = event.deltaY < 0
      const newBars = zoomIn
        ? currentBars * (1 - factor)
        : currentBars * (1 + factor)

      // 限制範圍
      const clampedBars = Math.max(minBars, Math.min(maxBars, newBars))

      // 固定右邊緣，只調整左邊緣
      this.chart.timeScale().setVisibleLogicalRange({
        from: currentRange.to - clampedBars,
        to: currentRange.to
      })
    }

    this.containerTarget.addEventListener('wheel', this.wheelHandler, { passive: false })
  }

  _updateRangeButtonStyles() {
    if (!this.hasRangeButtonTarget) {
      return
    }

    const { active, inactive } = this.constructor.BUTTON_CLASSES

    this.rangeButtonTargets.forEach(button => {
      const isActive = button.dataset.range === this.currentRange

      // 移除所有狀態 class
      button.classList.remove(...active, ...inactive)

      // 加入對應狀態 class
      if (isActive) {
        button.classList.add(...active)
      } else {
        button.classList.add(...inactive)
      }
    })
  }

  _renderLoadingState() {
    this._cleanup()
    this.containerTarget.innerHTML = `
      <div class="flex flex-col items-center justify-center h-full text-slate-400">
        <svg class="animate-spin h-6 w-6 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="text-sm">載入中...</span>
      </div>
    `
  }

  _renderNoDataMessage() {
    this._cleanup()
    this.containerTarget.innerHTML = `
      <div class="flex flex-col items-center justify-center h-full text-slate-400">
        <svg class="h-10 w-10 mb-3 text-slate-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <span class="text-sm font-medium text-slate-500">暫無數據</span>
        <span class="text-xs text-slate-400 mt-1">此期間無交易紀錄</span>
      </div>
    `
  }

  _renderErrorMessage() {
    this._cleanup()
    this.containerTarget.innerHTML = `
      <div class="flex flex-col items-center justify-center h-full text-slate-400">
        <svg class="h-10 w-10 mb-3 text-red-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span class="text-sm font-medium text-slate-500">載入失敗</span>
        <span class="text-xs text-slate-400 mt-1">請稍後再試</span>
      </div>
    `
  }

  // 根據目前螢幕寬度計算響應式 layout 設定
  // 用於 _createChart 和 _setupResizeHandler，確保響應式邏輯只存在於一處
  _getResponsiveLayout() {
    const { breakpoint, fontSize } = this.constructor.RESPONSIVE
    const isMobile = window.innerWidth < breakpoint
    return {
      fontSize: isMobile ? fontSize.mobile : fontSize.desktop
    }
  }

  // === Legend Methods ===
  // OHLCV 資訊面板，顯示於圖表左上角

  /**
   * 渲染 Legend DOM 元素到圖表容器
   * 在圖表建立後呼叫，建立固定位置的 Legend 面板
   * 若 DOM 中已有 legend target 則不重複建立
   */
  _renderLegendElement() {
    // 若 DOM 中已定義 legend target，直接使用
    if (this.hasLegendTarget) {
      return
    }

    // 動態建立 legend 元素
    const legendEl = document.createElement('div')
    legendEl.setAttribute('data-cb-chart-target', 'legend')
    legendEl.className = 'absolute top-2 left-2 z-10 font-mono text-xs leading-relaxed pointer-events-none'

    // 確保容器有 relative 定位供 absolute 元素參照
    this.containerTarget.style.position = 'relative'
    this.containerTarget.appendChild(legendEl)
  }

  /**
   * 設定 crosshair 移動事件訂閱
   * 訂閱圖表 crosshair 移動事件，更新 Legend 顯示
   * 滑鼠離開圖表區域時顯示最新資料
   */
  _setupLegendHandler() {
    this.crosshairMoveHandler = (param) => {
      // param.time 為 undefined 表示滑鼠離開圖表區域
      if (!param.time) {
        this._updateLegend(this._getLatestLegendData())
        return
      }

      const legendData = this.constructor.buildLegendData(
        param,
        this.candlestickSeries,
        this.volumeSeries
      )
      this._updateLegend(legendData || this._getLatestLegendData())
    }

    this.chart.subscribeCrosshairMove(this.crosshairMoveHandler)
  }

  /**
   * 取得最新一筆 K 線資料用於預設顯示
   * 從儲存的原始資料中取得最後一筆，轉換為 Legend 格式
   *
   * @returns {Object|null} 最新的 Legend 資料物件
   */
  _getLatestLegendData() {
    if (!this.chartData || this.chartData.length === 0) {
      return null
    }

    const latest = this.chartData[this.chartData.length - 1]
    return {
      time: latest.time,
      open: latest.open,
      high: latest.high,
      low: latest.low,
      close: latest.close,
      volume: latest.volume || 0,
      isUp: latest.close >= latest.open
    }
  }

  /**
   * 更新 Legend DOM 內容
   * 根據提供的資料更新 Legend 顯示，包含 OHLCV 值與對應顏色
   *
   * @param {Object|null} legendData - 要顯示的資料物件
   */
  _updateLegend(legendData) {
    if (!this.hasLegendTarget) {
      return
    }

    // 無資料時清空
    if (!legendData) {
      this.legendTarget.innerHTML = ''
      return
    }

    const colors = this.constructor.COLORS
    const priceColor = legendData.isUp ? colors.up : colors.down
    const formatPrice = this.constructor.formatPrice
    const formatVolume = this.constructor.formatVolume
    const formatDate = this.constructor.formatLegendDate

    this.legendTarget.innerHTML = `
      <div class="flex flex-wrap items-center gap-x-3 gap-y-0.5">
        <span class="text-slate-500">${formatDate(legendData.time)}</span>
        <span>
          <span class="text-slate-400">O</span>
          <span style="color: ${priceColor}">${formatPrice(legendData.open)}</span>
        </span>
        <span>
          <span class="text-slate-400">H</span>
          <span style="color: ${priceColor}">${formatPrice(legendData.high)}</span>
        </span>
        <span>
          <span class="text-slate-400">L</span>
          <span style="color: ${priceColor}">${formatPrice(legendData.low)}</span>
        </span>
        <span>
          <span class="text-slate-400">C</span>
          <span style="color: ${priceColor}">${formatPrice(legendData.close)}</span>
        </span>
        <span>
          <span class="text-slate-400">Vol</span>
          <span class="text-slate-600">${formatVolume(legendData.volume)}</span>
        </span>
      </div>
    `
  }

  _cleanup() {
    // 先取消 crosshair 訂閱（需在 chart.remove() 之前）
    if (this.chart && this.crosshairMoveHandler) {
      this.chart.unsubscribeCrosshairMove(this.crosshairMoveHandler)
      this.crosshairMoveHandler = null
    }

    if (this.chart) {
      this.chart.remove()
      this.chart = null
      this.candlestickSeries = null
      this.volumeSeries = null
    }

    // 清除圖表資料參考
    this.chartData = null

    // 清除待執行的 debounced resize，避免 memory leak
    if (this._resizeTimeout) {
      clearTimeout(this._resizeTimeout)
      this._resizeTimeout = undefined
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }

    if (this.windowResizeHandler) {
      window.removeEventListener('resize', this.windowResizeHandler)
      this.windowResizeHandler = null
    }

    if (this.wheelHandler) {
      this.containerTarget.removeEventListener('wheel', this.wheelHandler)
      this.wheelHandler = null
    }

    this.chartLoaded = false
  }
}
